import React, { useState, useEffect, useRef } from 'react';
import { adminApiService } from '../../services/adminApiService';
import { productService } from '../../services/productService';
import { FileTooltip } from '../common/FileTooltip';
import { FilePreviewModal } from '../common/FilePreviewModal';
import type { ComplianceAlert } from '../../services/adminApiService';
import { API_BASE } from '../../constants/app.config';
import './AdminComponents.css';
import { useAppSelector } from '../../store/hooks';
import { ApiResponse, AdminCustomer, KycSubmission, KybSubmission } from '../../services/adminApiService';
import UserDetailsModal from './UserDetailsModal';
import KybDetailsModal from './KybDetailsModal';
import KycDetailsModal from './KycDetailsModal';

const ComplianceAlerts: React.FC = React.memo(() => {
  const [previewFile, setPreviewFile] = useState<{
    isOpen: boolean;
    url: string;
    fileName: string;
    contentType?: string;
  }>({
    isOpen: false,
    url: '',
    fileName: '',
    contentType: undefined
  });
  const [alerts, setAlerts] = useState<ComplianceAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [assignModal, setAssignModal] = useState<{
    show: boolean;
    alertId: string;
    assigneeId: string;
  }>({
    show: false,
    alertId: '',
    assigneeId: ''
  });
  const [expandedAlertId, setExpandedAlertId] = useState<number | null>(null);
  const [expandedUser, setExpandedUser] = useState<AdminCustomer | null>(null);
  const [expandedLoading, setExpandedLoading] = useState(false);
  
  // Product filtering state
  const [products, setProducts] = useState<any[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | undefined>(undefined);
  const [productsLoading, setProductsLoading] = useState(false);
  
  const adminApi = adminApiService;
  
  // User Details Modal state
  const [userDetailsModal, setUserDetailsModal] = useState<{
    isOpen: boolean;
    alert: ComplianceAlert | null;
  }>({
    isOpen: false,
    alert: null
  });
  
  // Add refs to track component lifecycle and prevent unnecessary API calls
  const isInitializedRef = useRef(false);
  const lastLoadParamsRef = useRef<string>('');

  // Component lifecycle debugging
  useEffect(() => {
    console.log('ComplianceAlerts component mounted');
    return () => {
      console.log('ComplianceAlerts component unmounted');
    };
  }, []);

  // Add render debugging
  console.log('ComplianceAlerts component rendering with state:', {
    currentPage,
    severityFilter,
    statusFilter,
    sortBy,
    sortDirection,
    loading,
    alertsCount: alerts.length
  });

  useEffect(() => {
    console.log('ComplianceAlerts useEffect triggered with params:', {
      currentPage,
      severityFilter,
      statusFilter,
      sortBy,
      sortDirection
    });
    
    // Create a unique key for the current parameters
    const paramsKey = `${currentPage}-${severityFilter}-${statusFilter}-${sortBy}-${sortDirection}-${selectedProductId || 'all'}`;
    
    // Only load if parameters actually changed
    if (lastLoadParamsRef.current === paramsKey && isInitializedRef.current) {
      console.log('ComplianceAlerts: Parameters unchanged, skipping load');
      return;
    }
    
    lastLoadParamsRef.current = paramsKey;
    isInitializedRef.current = true;
    
    console.log('ComplianceAlerts: Loading alerts with new parameters');
    loadAlerts();
  }, [currentPage, severityFilter, statusFilter, sortBy, sortDirection, selectedProductId]);

  const loadAlerts = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('Loading alerts with params:', {
        page: currentPage - 1,
        size: 100,
        severity: severityFilter === 'all' ? undefined : severityFilter,
        status: statusFilter === 'all' ? undefined : statusFilter,
        productId: selectedProductId
      });

      const response = await adminApi.getComplianceAlerts(
        currentPage - 1,
        100,
        severityFilter === 'all' ? undefined : severityFilter,
        statusFilter === 'all' ? undefined : statusFilter,
        selectedProductId
      );

      console.log('Raw API response:', response);

      if (response.success && response.data) {
        console.log('Backend returned alerts:', response.data.content.length);
        console.log('Sample alert from backend:', response.data.content[0]);
        
        // Map backend alert fields to frontend structure with all required fields
        const mappedAlerts = response.data.content.map(alert => {
          const mappedAlert = {
            id: alert.id,
            alertId: alert.alertId || alert.id, // fallback to id if alertId missing
            type: alert.alertType || alert.type || '',
            severity: alert.severity,
            description: alert.description,
            customerId: alert.customerId,
            customerName: alert.customerName || '',
            status: alert.status,
            assignee: alert.assignedTo || alert.assignee || '',
            assigneeName: alert.assignedTo || alert.assignee || '',
            createdAt: alert.createdAt,
            title: alert.title || '',
            requiresAction: alert.requiresAction ?? false,
          };
          console.log('Mapped alert:', mappedAlert);
          return mappedAlert;
        });
        
        console.log('Total mapped alerts:', mappedAlerts.length);
        setAlerts(mappedAlerts as ComplianceAlert[]);
        setTotalPages(response.data.totalPages);
      } else {
        console.error('API response error:', response.error);
        setError(response.error || 'Failed to load compliance alerts');
      }
    } catch (err) {
      console.error('Exception loading alerts:', err);
      setError('Failed to load compliance alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignAlert = async () => {
    if (!assignModal.assigneeId.trim()) {
      alert('Please enter an assignee ID');
      return;
    }

    try {
      const response = await adminApi.assignAlert(assignModal.alertId, assignModal.assigneeId);
      if (response.success) {
        setAssignModal({ show: false, alertId: '', assigneeId: '' });
        await loadAlerts();
      } else {
        alert(response.error || 'Failed to assign alert');
      }
    } catch (err) {
      alert('Failed to assign alert');
    }
  };

  const handleUpdateStatus = async (alertId: number, status: string, notes?: string) => {
    try {
      const response = await adminApi.updateAlertStatus(alertId.toString(), status, notes);
      if (response.success) {
        await loadAlerts();
        // Close modal if it's open
        if (userDetailsModal.isOpen) {
          setUserDetailsModal({ isOpen: false, alert: null });
        }
      } else {
        alert(response.error || 'Failed to update alert status');
      }
    } catch (err) {
      alert('Failed to update alert status');
    }
  };

  const handleApproveAlert = async (alertId: number) => {
    try {
      const response = await adminApi.resolveComplianceAlert(alertId.toString(), 'APPROVED', 'Alert approved by admin');
      if (response.success) {
        await loadAlerts();
        // Close modal if it's open
        if (userDetailsModal.isOpen) {
          setUserDetailsModal({ isOpen: false, alert: null });
        }
      } else {
        alert(response.error || 'Failed to approve alert');
      }
    } catch (err) {
      alert('Failed to approve alert');
    }
  };

  const handleRejectAlert = async (alertId: number) => {
    try {
      const response = await adminApi.resolveComplianceAlert(alertId.toString(), 'REJECTED', 'Alert rejected by admin');
      if (response.success) {
        await loadAlerts();
        // Close modal if it's open
        if (userDetailsModal.isOpen) {
          setUserDetailsModal({ isOpen: false, alert: null });
        }
      } else {
        alert(response.error || 'Failed to reject alert');
      }
    } catch (err) {
      alert('Failed to reject alert');
    }
  };

  const handleRequestMoreInfoAlert = async (alertId: number) => {
    try {
      const response = await adminApi.resolveComplianceAlert(
        alertId.toString(),
        'REQUIRES_ADDITIONAL_INFO',
        'Additional information requested by compliance'
      );
      if (response.success) {
        await loadAlerts();
        if (userDetailsModal.isOpen) {
          setUserDetailsModal({ isOpen: false, alert: null });
        }
      } else {
        alert(response.error || 'Failed to update alert');
      }
    } catch (err) {
      alert('Failed to update alert');
    }
  };

  const handleAssignFromModal = async (alertId: string, assigneeId: string) => {
    try {
      const response = await adminApi.assignAlert(alertId, assigneeId);
      if (response.success) {
        await loadAlerts();
        // Close modal if it's open
        if (userDetailsModal.isOpen) {
          setUserDetailsModal({ isOpen: false, alert: null });
        }
      } else {
        alert(response.error || 'Failed to assign alert');
      }
    } catch (err) {
      alert('Failed to assign alert');
    }
  };

  const openUserDetailsModal = (alert: ComplianceAlert) => {
    setUserDetailsModal({
      isOpen: true,
      alert: alert
    });
  };

  const openAssignModal = (alertId: number) => {
    setAssignModal({
      show: true,
      alertId: alertId.toString(),
      assigneeId: ''
    });
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSeverityBadgeClass = (severity: string) => {
    switch (severity) {
      case 'HIGH': return 'status-badge rejected';
      case 'MEDIUM': return 'status-badge pending';
      case 'LOW': return 'status-badge active';
      default: return 'status-badge';
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'OPEN': return 'status-badge pending';
      case 'IN_PROGRESS': return 'status-badge active';
      case 'RESOLVED': return 'status-badge approved';
      case 'DISMISSED': return 'status-badge inactive';
      default: return 'status-badge';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'HIGH': return '🚨';
      case 'MEDIUM': return '⚠️';
      case 'LOW': return 'ℹ️';
      default: return '📋';
    }
  };

  // Risk level calculation based on industry
  const calculateRiskFromIndustry = (industry?: string): 'low' | 'medium' | 'high' => {
    if (!industry) return 'low'; // Default for personal KYC (no industry)
    
    const industryLower = industry.toLowerCase();
    
    // High-risk industries (matches backend logic)
    const highRiskKeywords = ['crypto', 'gambling', 'betting', 'forex', 'adult', 'casino', 'poker', 'lottery', 'binary options', 'cfd'];
    if (highRiskKeywords.some(keyword => industryLower.includes(keyword))) {
      return 'high';
    }
    
    // Low-risk industries (traditional, well-regulated sectors)
    const lowRiskKeywords = ['retail', 'hospitality', 'restaurant', 'professional services', 'consulting', 'education', 'healthcare', 'non-profit'];
    if (lowRiskKeywords.some(keyword => industryLower.includes(keyword))) {
      return 'low';
    }
    
    // Default to medium for all other industries
    return 'medium';
  };

  // Get risk level for a submission (with fallback to calculated value)
  const getRiskLevel = (submissionId: number, industry: string | undefined, type: 'kyc' | 'kyb'): 'low' | 'medium' | 'high' => {
    const riskMap = type === 'kyc' ? kycRiskLevels : kybRiskLevels;
    if (riskMap[submissionId]) {
      return riskMap[submissionId];
    }
    return calculateRiskFromIndustry(industry);
  };

  // Update risk level
  const handleRiskLevelChange = async (submissionId: number, newRiskLevel: 'low' | 'medium' | 'high', type: 'kyc' | 'kyb') => {
    // Update local state immediately for UI responsiveness
    if (type === 'kyc') {
      const updated = { ...kycRiskLevels, [submissionId]: newRiskLevel };
      setKycRiskLevels(updated);
      // Persist to localStorage
      try {
        localStorage.setItem('kycRiskLevels', JSON.stringify(updated));
      } catch (error) {
        console.error('Failed to save KYC risk levels to localStorage:', error);
      }
      // Save to backend - only send riskLevel, status and comments are optional
      try {
        await adminApi.reviewKycSubmission(submissionId, undefined, undefined, newRiskLevel.toUpperCase());
        console.log(`Risk level saved to backend for KYC submission ${submissionId}: ${newRiskLevel}`);
      } catch (error) {
        console.error('Failed to save KYC risk level to backend:', error);
      }
    } else {
      const updated = { ...kybRiskLevels, [submissionId]: newRiskLevel };
      setKybRiskLevels(updated);
      // Persist to localStorage
      try {
        localStorage.setItem('kybRiskLevels', JSON.stringify(updated));
      } catch (error) {
        console.error('Failed to save KYB risk levels to localStorage:', error);
      }
      // Save to backend - only send riskLevel, status and comments are optional
      try {
        await adminApi.reviewKybSubmission(submissionId, undefined, undefined, newRiskLevel.toUpperCase());
        console.log(`Risk level saved to backend for KYB submission ${submissionId}: ${newRiskLevel}`);
      } catch (error) {
        console.error('Failed to save KYB risk level to backend:', error);
      }
    }
  };

  // Get risk badge class for styling
  const getRiskBadgeClass = (riskLevel: 'low' | 'medium' | 'high') => {
    switch (riskLevel) {
      case 'high': return 'status-badge rejected';
      case 'medium': return 'status-badge pending';
      case 'low': return 'status-badge active';
      default: return 'status-badge';
    }
  };

  const handleRowClick = async (alert: ComplianceAlert) => {
    console.log('Row clicked:', alert);
    if (expandedAlertId === alert.id) {
      setExpandedAlertId(null);
      setExpandedUser(null);
      return;
    }
    setExpandedAlertId(alert.id);
    setExpandedLoading(true);
    try {
      if (alert.customerId) {
        console.log('Fetching user for customerId:', alert.customerId);
        const response = await adminApi.getCustomerById(alert.customerId);
        console.log('API response:', response);
        if (response.success && response.data) {
          setExpandedUser(response.data);
        } else {
          setExpandedUser(null);
        }
      } else {
        console.log('No customerId on alert:', alert);
        setExpandedUser(null);
      }
    } finally {
      setExpandedLoading(false);
    }
  };

  const [activeTab, setActiveTab] = useState<'alerts' | 'kyc' | 'kyb'>(() => {
    try {
      const stored = window.sessionStorage.getItem('complianceActiveSubTab');
      if (stored === 'kyc' || stored === 'kyb' || stored === 'alerts') {
        // clear after read so future navigations default normally
        window.sessionStorage.removeItem('complianceActiveSubTab');
        return stored as 'alerts' | 'kyc' | 'kyb';
      }
    } catch {}
    return 'alerts';
  });

  // KYC/KYB state
  const [kycSubmissions, setKycSubmissions] = useState<KycSubmission[]>([]);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycError, setKycError] = useState<string | null>(null);
  const [kybSubmissions, setKybSubmissions] = useState<KybSubmission[]>([]);
  const [kybLoading, setKybLoading] = useState(false);
  const [kybError, setKybError] = useState<string | null>(null);
  const [kycPage, setKycPage] = useState(1);
  const [kycTotalPages, setKycTotalPages] = useState(1);
  const [kybPage, setKybPage] = useState(1);
  const [kybTotalPages, setKybTotalPages] = useState(1);
  const [selectedKyb, setSelectedKyb] = useState<KybSubmission | null>(null);
  // Attachments modal state
  const [isDocsOpen, setIsDocsOpen] = useState(false);
  const [docsTarget, setDocsTarget] = useState<{ kind: 'kyc' | 'kyb'; id: number } | null>(null);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Array<{ id: number; type: string; note?: string; documentId: string; filename?: string; contentType?: string; sizeBytes?: number; uploadedBy?: string; uploadedAt?: string }>>([]);
  // Pagination for submission documents modal
  const [docsPage, setDocsPage] = useState(1);
  const docsPageSize = 5;
  const docsTotalPages = Math.max(1, Math.ceil(attachments.length / docsPageSize));
  const pagedAttachments = attachments.slice((docsPage - 1) * docsPageSize, docsPage * docsPageSize);
  // Version bump to force remount of table when list changes
  const [docsVersion, setDocsVersion] = useState(0);

  // Clamp current page if list size changes (e.g., after delete/upload)
  useEffect(() => {
    if (docsPage > docsTotalPages) setDocsPage(docsTotalPages);
  }, [attachments, docsTotalPages]);

  const openAttachment = async (att: { documentId: string; filename?: string; contentType?: string }) => {
    try {
      const url = `${API_BASE}/admin/files-simple/${att.documentId}`;
      const blob = await adminApiService.getFileBlobByUrl(url);
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank', 'noopener');
      // Revoke after some time to allow the new tab to load
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (e) {
      alert('Failed to open file');
    }
  };

  const downloadAttachment = async (att: { documentId: string; filename?: string; contentType?: string }) => {
    try {
      const url = `${API_BASE}/admin/files-simple/${att.documentId}`;
      const blob = await adminApiService.getFileBlobByUrl(url);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = att.filename || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch (e) {
      alert('Failed to download file');
    }
  };
  // Upload modal state
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState<{ kind: 'kyc' | 'kyb'; id: number } | null>(null);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadType, setUploadType] = useState<string>('OTHER');
  const [uploadNote, setUploadNote] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  
  // Status filters for KYC and KYB
  const [kycStatusFilter, setKycStatusFilter] = useState<string>('');
  const [kybStatusFilter, setKybStatusFilter] = useState<string>('');
  
  // KYC Details Modal state
  const [selectedKyc, setSelectedKyc] = useState<any | null>(null);
  const [kycDetailsLoading, setKycDetailsLoading] = useState(false);

  // Risk level state management (maps submission ID to risk level)
  // Load from localStorage on mount
  const [kycRiskLevels, setKycRiskLevels] = useState<Record<number, 'low' | 'medium' | 'high'>>(() => {
    try {
      const stored = localStorage.getItem('kycRiskLevels');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  const [kybRiskLevels, setKybRiskLevels] = useState<Record<number, 'low' | 'medium' | 'high'>>(() => {
    try {
      const stored = localStorage.getItem('kybRiskLevels');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Load products on component mount
  const [currentAdmin, setCurrentAdmin] = useState<any | null>(null);
  const hasPermission = (perm: string) => {
    const p = currentAdmin?.permissions || [];
    const role = currentAdmin?.role;
    if (role === 'SUPER_ADMIN') return true;
    return Array.isArray(p) && p.includes(perm);
  };
  useEffect(() => {
    const loadProducts = async () => {
      setProductsLoading(true);
      try {
        console.log('Loading products for admin filter...');
        
        // Get current admin user profile to check assigned products
        const profileResponse = await adminApi.getAdminProfile();
        console.log('Admin profile response:', profileResponse);
        
        if (profileResponse.success && profileResponse.data) {
          const adminUser = profileResponse.data;
          setCurrentAdmin(adminUser);
          console.log('Current admin user:', adminUser);
          console.log('Admin user assignedProducts field:', adminUser.assignedProducts);
          console.log('Admin user assignedProducts type:', typeof adminUser.assignedProducts);
          console.log('Admin user assignedProducts length:', adminUser.assignedProducts ? adminUser.assignedProducts.length : 'undefined');
          console.log('Full profile response:', profileResponse);
          console.log('Profile response data keys:', Object.keys(adminUser));
          
          // If user has assigned products, filter by customer type based on active tab
          if (adminUser.assignedProducts && adminUser.assignedProducts.length > 0) {
            console.log('Admin has assigned products:', adminUser.assignedProducts);
            const filteredProducts = adminUser.assignedProducts.filter((product: any) => {
              if (activeTab === 'kyc') {
                return product.customerType === 'PERSONAL';
              } else if (activeTab === 'kyb') {
                return product.customerType === 'BUSINESS';
              }
              return true; // For alerts tab, show all
            });
            setProducts(filteredProducts);
          } else if (adminUser.role === 'SUPER_ADMIN') {
            // SUPER_ADMIN can see all products, but filter by customer type
            console.log('SUPER_ADMIN - loading products for', activeTab);
            if (activeTab === 'kyc') {
              const personalResponse = await productService.getPersonalProducts();
              setProducts(personalResponse.success && personalResponse.data ? personalResponse.data : []);
            } else if (activeTab === 'kyb') {
              const businessResponse = await productService.getBusinessProducts();
              setProducts(businessResponse.success && businessResponse.data ? businessResponse.data : []);
            } else {
              // For alerts tab, load all products
              const [personalResponse, businessResponse] = await Promise.all([
                productService.getPersonalProducts(),
                productService.getBusinessProducts()
              ]);
              
              const allProducts = [
                ...(personalResponse.success && personalResponse.data ? personalResponse.data : []),
                ...(businessResponse.success && businessResponse.data ? businessResponse.data : [])
              ];
              
              setProducts(allProducts);
            }
          } else {
            // Other roles see no products
            console.log('No assigned products for this admin user');
            setProducts([]);
          }
        } else {
          console.error('Failed to get admin profile');
          setProducts([]);
        }
      } catch (error) {
        console.error('Failed to load products:', error);
        setProducts([]);
      } finally {
        setProductsLoading(false);
        // Ensure the active tab is permitted; if not, switch to the first permitted tab
        const canBasic = hasPermission('VIEW_ADMIN_COMPLIANCE_BASIC');
        const canKyc = hasPermission('VIEW_ADMIN_COMPLIANCE_KYC_REVIEW');
        const canKyb = hasPermission('VIEW_ADMIN_COMPLIANCE_KYB_REVIEW');
        const wanted = activeTab;
        const allowed = (wanted === 'alerts' && canBasic) || (wanted === 'kyc' && canKyc) || (wanted === 'kyb' && canKyb);
        if (!allowed) {
          if (canBasic) setActiveTab('alerts');
          else if (canKyc) setActiveTab('kyc');
          else if (canKyb) setActiveTab('kyb');
        }
      }
    };
    
    loadProducts();
  }, [activeTab]);

  // Close any open modals when switching tabs to avoid stale overlays
  useEffect(() => {
    if (isDocsOpen) {
      setIsDocsOpen(false);
      setDocsTarget(null);
      setAttachments([]);
      setDocsPage(1);
    }
    if (isUploadOpen) {
      setIsUploadOpen(false);
      setUploadTarget(null);
      setUploadFiles([]);
      setUploading(false);
    }
    if (previewFile.isOpen) {
      setPreviewFile({ isOpen: false, url: '', fileName: '', contentType: undefined });
    }
  }, [activeTab]);

  // Function to load KYC submissions
  const loadKycSubmissions = async () => {
    setKycLoading(true);
    setKycError(null);
    const statusParam = kycStatusFilter && kycStatusFilter.trim() !== '' ? kycStatusFilter : undefined;
    console.log('Fetching KYC submissions with productId:', selectedProductId, 'status:', statusParam);
    try {
      const res = await adminApi.getKycSubmissions(kycPage - 1, 10, statusParam, selectedProductId);
      console.log('KYC submissions response:', res);
      if (res.success && res.data) {
        // Map API response to match KycSubmission interface
        const mappedSubmissions = res.data.content.map((sub: any) => ({
          ...sub,
          submittedAt: sub.uploadDate || sub.submittedAt, // Map uploadDate to submittedAt
          userName: sub.userName || (sub.firstName && sub.lastName ? `${sub.firstName} ${sub.lastName}` : sub.email || `User ${sub.userId}`) // Construct userName from firstName/lastName if needed
        }));
        
        // Initialize risk levels from API or localStorage, with fallback to calculated/default
        const newRiskLevels: Record<number, 'low' | 'medium' | 'high'> = {};
        mappedSubmissions.forEach((sub: any) => {
          // Priority: 1. API response, 2. localStorage, 3. calculated default
          if (sub.riskLevel) {
            // Convert backend format (LOW/MEDIUM/HIGH) to frontend format (low/medium/high)
            newRiskLevels[sub.id] = sub.riskLevel.toLowerCase() as 'low' | 'medium' | 'high';
          } else if (kycRiskLevels[sub.id]) {
            // Use existing localStorage value
            newRiskLevels[sub.id] = kycRiskLevels[sub.id];
          } else {
            // Default to low for personal KYC
            newRiskLevels[sub.id] = 'low';
          }
        });
        if (Object.keys(newRiskLevels).length > 0) {
          setKycRiskLevels(prev => ({ ...prev, ...newRiskLevels }));
        }
        
        setKycSubmissions(mappedSubmissions);
        setKycTotalPages(res.data.totalPages || 1);
      } else {
        setKycError(res.error || 'Failed to load KYC submissions');
      }
    } catch (error) {
      setKycError('Failed to load KYC submissions');
    } finally {
      setKycLoading(false);
    }
  };

  // Fetch KYC submissions
  useEffect(() => {
    if (activeTab !== 'kyc') return;
    loadKycSubmissions();
  }, [activeTab, kycPage, selectedProductId, kycStatusFilter]);

  // Function to load KYB submissions
  const loadKybSubmissions = async () => {
    setKybLoading(true);
    setKybError(null);
    const statusParam = kybStatusFilter && kybStatusFilter.trim() !== '' ? kybStatusFilter : undefined;
    console.log('Fetching KYB submissions with productId:', selectedProductId, 'status:', statusParam);
    try {
      const res = await adminApi.getKybSubmissions(kybPage - 1, 10, statusParam, selectedProductId);
      console.log('KYB submissions response:', res);
      if (res.success && res.data) {
        // Map API response to match KybSubmission interface
        const mappedSubmissions = res.data.content.map((sub: any) => ({
          ...sub,
          submittedAt: sub.uploadDate || sub.submittedAt, // Map uploadDate to submittedAt
          userName: sub.userName || `User ${sub.userId}`, // Fallback if userName not provided
          email: sub.email || `user${sub.userId}@example.com`, // Fallback if email not provided
        }));
        
        // Initialize risk levels from API or localStorage, with fallback to calculated
        const newRiskLevels: Record<number, 'low' | 'medium' | 'high'> = {};
        mappedSubmissions.forEach((sub: any) => {
          // Priority: 1. API response, 2. localStorage, 3. calculated from industry
          if (sub.riskLevel) {
            // Convert backend format (LOW/MEDIUM/HIGH) to frontend format (low/medium/high)
            newRiskLevels[sub.id] = sub.riskLevel.toLowerCase() as 'low' | 'medium' | 'high';
          } else if (kybRiskLevels[sub.id]) {
            // Use existing localStorage value
            newRiskLevels[sub.id] = kybRiskLevels[sub.id];
          } else {
            // Calculate from industry
            newRiskLevels[sub.id] = calculateRiskFromIndustry(sub.industry);
          }
        });
        if (Object.keys(newRiskLevels).length > 0) {
          setKybRiskLevels(prev => ({ ...prev, ...newRiskLevels }));
        }
        
        setKybSubmissions(mappedSubmissions);
        setKybTotalPages(res.data.totalPages || 1);
      } else {
        setKybError(res.error || 'Failed to load KYB submissions');
      }
    } catch (error) {
      setKybError('Failed to load KYB submissions');
    } finally {
      setKybLoading(false);
    }
  };

  // Fetch KYB submissions
  useEffect(() => {
    if (activeTab !== 'kyb') return;
    loadKybSubmissions();
  }, [activeTab, kybPage, selectedProductId, kybStatusFilter]);

  // Approve/Reject handlers
  const handleKycStatus = async (id: number, status: string) => {
    try {
      console.log(`Updating KYC submission ${id} to ${status}`);
      const response = await adminApi.reviewKycSubmission(id, status, `Status updated to ${status} by admin`);
      
      if (response.success) {
        console.log(`Successfully updated KYC submission ${id} to ${status}`);
        // Refresh the KYC submissions list
        await loadKycSubmissions();
        alert(`KYC submission ${id} has been ${status.toLowerCase()}`);
      } else {
        console.error('Failed to update KYC submission:', response.error);
        alert(`Failed to update KYC submission: ${response.error}`);
      }
    } catch (error) {
      console.error('Error updating KYC submission:', error);
      alert(`Error updating KYC submission: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleViewKycDetails = async (submission: KycSubmission) => {
    setKycDetailsLoading(true);
    try {
      const response = await adminApiService.getKycSubmissionDetails(submission.id);
      if (response.success && response.data) {
        setSelectedKyc(response.data);
      } else {
        console.error('Failed to fetch KYC details:', response.error);
        // Fallback to basic submission data
        setSelectedKyc(submission);
      }
    } catch (error) {
      console.error('Error fetching KYC details:', error);
      // Fallback to basic submission data
      setSelectedKyc(submission);
    } finally {
      setKycDetailsLoading(false);
    }
  };
  const handleKybStatus = async (id: number, status: string) => {
    try {
      console.log(`Updating KYB submission ${id} to ${status}`);
      const response = await adminApi.reviewKybSubmission(id, status, `Status updated to ${status} by admin`);
      
      if (response.success) {
        console.log(`Successfully updated KYB submission ${id} to ${status}`);
        // Refresh the KYB submissions list
        await loadKybSubmissions();
        alert(`KYB submission ${id} has been ${status.toLowerCase()}`);
      } else {
        console.error('Failed to update KYB submission:', response.error);
        alert(`Failed to update KYB submission: ${response.error}`);
      }
    } catch (error) {
      console.error('Error updating KYB submission:', error);
      alert(`Error updating KYB submission: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleViewKybDetails = async (submission: KybSubmission) => {
    try {
      const response = await adminApiService.getKybSubmissionDetails(submission.id);
      if (response.success && response.data) {
        setSelectedKyb(response.data as any);
      } else {
        // Fallback to basic submission data
        setSelectedKyb(submission as any);
      }
    } catch (error) {
      // Fallback to basic submission data on error
      setSelectedKyb(submission as any);
    }
  };

  const openDocsModal = async (kind: 'kyc' | 'kyb', id: number) => {
    setDocsTarget({ kind, id });
    setIsDocsOpen(true);
    setDocsLoading(true);
    setDocsError(null);
    setDocsPage(1);
    try {
      const res = kind === 'kyc' ? await adminApi.listKycDocuments(id) : await adminApi.listKybDocuments(id);
      if (res.success && res.data) {
        setAttachments(res.data as any);
        setDocsVersion(v => v + 1);
      } else {
        setAttachments([]);
        setDocsError(res.error || 'Failed to load documents');
      }
    } catch (e: any) {
      setDocsError(e.message || 'Failed to load documents');
      setAttachments([]);
    } finally {
      setDocsLoading(false);
    }
  };

  const refreshDocsList = async (kind: 'kyc' | 'kyb', id: number) => {
    const res = kind === 'kyc' ? await adminApi.listKycDocuments(id) : await adminApi.listKybDocuments(id);
    if (res.success && res.data) {
      setAttachments(res.data as any);
      // After refresh, ensure page is valid
      const total = (res.data as any[]).length;
      const totalPages = Math.max(1, Math.ceil(total / docsPageSize));
      if (docsPage > totalPages) setDocsPage(totalPages);
      // Reset to first page so the user sees the latest items immediately
      if (docsPage !== 1) setDocsPage(1);
      // Force a re-mount of the table to break any memoization
      setDocsVersion(v => v + 1);
    }
  };

  const openUploadModal = (kind: 'kyc' | 'kyb', id: number) => {
    setUploadTarget({ kind, id });
    setUploadFiles([]);
    setUploadType('OTHER');
    setUploadNote('');
    setIsUploadOpen(true);
  };

  const submitUpload = async () => {
    if (!uploadTarget || uploadFiles.length === 0) {
      alert('Please select at least one file');
      return;
    }
    try {
      setUploading(true);
      // Build FormData explicitly to ensure file parts are present
      const form = new FormData();
      uploadFiles.forEach(f => form.append('files', f));
      if (uploadFiles[0]) form.append('file', uploadFiles[0]);
      uploadFiles.forEach(() => form.append('types', uploadType));
      uploadFiles.forEach(() => form.append('notes', uploadNote));

      console.log('Submitting upload: files=', uploadFiles.map(f => f.name), 'type=', uploadType, 'note=', uploadNote);
      const endpoint = uploadTarget.kind === 'kyc'
        ? `/admin/kyc-submissions/${uploadTarget.id}/documents`
        : `/admin/kyb-submissions/${uploadTarget.id}/documents`;
      const res = await adminApiService.postMultipart(endpoint, form);
      if (!res.success) throw new Error(res.error || 'Upload failed');

      // Refresh tables and, if the Submission Documents modal is open for this target, refresh its list too
      if (uploadTarget.kind === 'kyc') {
        await loadKycSubmissions();
        if (isDocsOpen && docsTarget?.kind === 'kyc' && docsTarget.id === uploadTarget.id) {
          await refreshDocsList('kyc', uploadTarget.id);
        }
      } else {
        await loadKybSubmissions();
        if (isDocsOpen && docsTarget?.kind === 'kyb' && docsTarget.id === uploadTarget.id) {
          await refreshDocsList('kyb', uploadTarget.id);
        }
      }
      setIsUploadOpen(false);
    } catch (e:any) {
      alert(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Tabbed interface
  console.log('ComplianceAlerts render - alerts state:', {
    alertsCount: alerts.length,
    alerts: alerts,
    loading,
    error,
    activeTab
  });
  
    return (
    <div className="compliance-alerts">
      <div className="compliance-tabs">
        {hasPermission('VIEW_ADMIN_COMPLIANCE_BASIC') && (
          <button
            className={activeTab === 'alerts' ? 'tab-btn active' : 'tab-btn'}
            onClick={() => setActiveTab('alerts')}
          >
            Basic Registration
          </button>
        )}
        {hasPermission('VIEW_ADMIN_COMPLIANCE_KYC_REVIEW') && (
          <button
            className={activeTab === 'kyc' ? 'tab-btn active' : 'tab-btn'}
            onClick={() => setActiveTab('kyc')}
          >
            Enhanced KYC Review
          </button>
        )}
        {hasPermission('VIEW_ADMIN_COMPLIANCE_KYB_REVIEW') && (
          <button
            className={activeTab === 'kyb' ? 'tab-btn active' : 'tab-btn'}
            onClick={() => setActiveTab('kyb')}
          >
            Enhanced KYB Review
          </button>
        )}
      </div>

      {activeTab === 'alerts' && hasPermission('VIEW_ADMIN_COMPLIANCE_BASIC') && (
        <>
          {loading ? (
      <div className="admin-component-loading">
        <div className="loading-spinner"></div>
        <p>Loading compliance alerts...</p>
      </div>
          ) : error ? (
      <div className="admin-component-error">
        <div className="error-icon">⚠️</div>
        <h3>Error Loading Compliance Alerts</h3>
        <p>{error}</p>
        <button className="retry-btn" onClick={loadAlerts}>
          Try Again
        </button>
      </div>
          ) : (
            <div className="admin-table-container">
              {/* Summary Header */}
              <div className="compliance-summary-header">
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                    Basic Registration
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                    Showing {alerts.length} alerts • Page {currentPage} of {totalPages}
                  </p>
        </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  Last updated: {new Date().toLocaleTimeString()}
        </div>
      </div>

          {/* Product Filter for Basic Registration */}
          <div className="compliance-filter-section">
            <label htmlFor="product-filter-alerts" style={{ fontWeight: 'bold', fontSize: '14px' }}>
              Filter by Product:
            </label>
            <select
              id="product-filter-alerts"
              value={selectedProductId !== undefined ? String(selectedProductId) : (currentAdmin?.role === 'SUPER_ADMIN' || (currentAdmin?.permissions || []).includes('VIEW_ADMIN_REPORTS') ? 'ALL' : '')}
              onChange={(e) => setSelectedProductId(e.target.value === 'ALL' ? undefined : (e.target.value ? Number(e.target.value) : undefined))}
              style={{ 
                padding: '8px 12px', 
                borderRadius: 4, 
                border: '1px solid #ddd', 
                minWidth: 240,
                fontSize: '14px'
              }}
              disabled={productsLoading}
            >
              {productsLoading ? (
                <option value="">Loading products...</option>
              ) : (
                <>
                  {(currentAdmin?.role === 'SUPER_ADMIN' || (currentAdmin?.permissions || []).includes('VIEW_ADMIN_REPORTS')) && (
                    <option value="ALL">All Products</option>
                  )}
                  {products.map((product: any) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

            {/* Mobile scroll indicator */}
            <div className="table-scroll-indicator">
              ← Swipe horizontally to see all columns →
            </div>
            <table className="admin-table">
          <thead>
            <tr>
                    <th>Alert ID</th>
                    <th>Type</th>
                    <th>Severity</th>
                    <th>Customer</th>
                    <th>Status</th>
                    <th>Assignee</th>
                    <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
                  {alerts.map(alert => (
                    <tr key={alert.id}>
                      <td data-label="Alert ID">{alert.alertId || alert.id}</td>
                      <td data-label="Type">{alert.type || alert.alertType || ''}</td>
                      <td data-label="Severity">{alert.severity}</td>
                      <td data-label="Customer">{alert.customerName || `Customer #${alert.customerId}`}</td>
                  <td data-label="Status">
                    <span className={getStatusBadgeClass(alert.status)}>
                          {alert.status}
                    </span>
                  </td>
                      <td data-label="Assignee">{alert.assignee || alert.assignedTo || 'Unassigned'}</td>
                      <td data-label="Created">{alert.createdAt ? new Date(alert.createdAt).toLocaleString() : ''}</td>
                    <td data-label="Actions">
                      <div className="action-btn-group compliance-actions">
                      {alert.status === 'OPEN' && (
                        <>
                          <button
                            className="action-btn success"
                                onClick={() => handleApproveAlert(alert.id)}
                            title="Approve Registration"
                          >
                            ✅ Approve
                          </button>
                          <button
                            className="action-btn danger"
                                onClick={() => handleRejectAlert(alert.id)}
                            title="Reject Registration"
                          >
                            ❌ Reject
                          </button>
                          {((alert.type || alert.alertType) === 'SUBSCRIPTION_UPGRADE'
                            || (alert.type || alert.alertType) === 'ADMIN_SUBSCRIPTION_RENEWAL') && (
                            <button
                              className="action-btn secondary"
                              onClick={() => handleRequestMoreInfoAlert(alert.id)}
                              title="Ask for more information before payment"
                            >
                              ℹ️ Request info
                            </button>
                          )}
                        </>
                      )}
                      <button
                        className="action-btn secondary"
                            onClick={() => openUserDetailsModal(alert)}
                        title="View Details"
                      >
                        👁️ View
                      </button>
                    </div>
                  </td>
                </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Debug Info */}
              <div style={{ padding: '10px', background: '#f0f0f0', fontSize: '12px', color: '#666' }}>
                {alerts.length > 0 && (
                  <span style={{ marginLeft: '20px' }}>
                    Status: {alerts.filter(a => a.status === 'OPEN').length} Open, 
                    {alerts.filter(a => a.status === 'RESOLVED').length} Resolved, 
                    {alerts.filter(a => a.status === 'IN_PROGRESS').length} In Progress
                  </span>
                )}
                            </div>
                            </div>
                          )}
        </>
      )}

      {activeTab === 'kyc' && hasPermission('VIEW_ADMIN_COMPLIANCE_KYC_REVIEW') && (
        <div className="compliance-tab-content">
          <div className="compliance-tab-header">
          <h2>Enhanced KYC Review</h2>
            <div className="compliance-filters">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label htmlFor="product-filter-kyc" style={{ fontWeight: 'bold' }}>Filter by Product:</label>
              <select
                id="product-filter-kyc"
                value={selectedProductId !== undefined ? String(selectedProductId) : (currentAdmin?.role === 'SUPER_ADMIN' || (currentAdmin?.permissions || []).includes('VIEW_ADMIN_REPORTS') ? 'ALL' : '')}
                  onChange={(e) => {
                    setSelectedProductId(e.target.value === 'ALL' ? undefined : (e.target.value ? Number(e.target.value) : undefined));
                    setKycPage(1); // Reset to page 1 when filter changes
                  }}
                style={{ padding: '8px 12px', borderRadius: 4, border: '1px solid #ddd', minWidth: 240 }}
                disabled={productsLoading}
              >
                {(currentAdmin?.role === 'SUPER_ADMIN' || (currentAdmin?.permissions || []).includes('VIEW_ADMIN_REPORTS')) && (
                  <option value="ALL">All Products</option>
                )}
                {productsLoading && <option disabled>Loading products...</option>}
                {products.length === 0 && !productsLoading && <option disabled>No products available</option>}
                {products.map(product => (
                  <option key={product.id} value={String(product.id)}>
                    {product.name} ({product.type})
                  </option>
                ))}
              </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label htmlFor="status-filter-kyc" style={{ fontWeight: 'bold' }}>Filter by Status:</label>
                <select
                  id="status-filter-kyc"
                  value={kycStatusFilter}
                  onChange={(e) => {
                    setKycStatusFilter(e.target.value);
                    setKycPage(1); // Reset to page 1 when filter changes
                  }}
                  style={{ padding: '8px 12px', borderRadius: 4, border: '1px solid #ddd', minWidth: 180 }}
                >
                  <option value="">All Statuses</option>
                  <option value="PENDING">PENDING</option>
                  <option value="APPROVED">APPROVED</option>
                  <option value="REJECTED">REJECTED</option>
                </select>
              </div>
            </div>
          </div>
          {kycLoading ? <div>Loading...</div> : kycError ? <div style={{ color: 'red' }}>{kycError}</div> : (
            <div className="admin-table-container">
              {/* Mobile scroll indicator */}
              <div className="table-scroll-indicator">
                ← Swipe horizontally to see all columns →
              </div>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Submitted</th>
                    <th>Utility Bill</th>
                    <th>Passport</th>
                    <th>ID Front</th>
                    <th>ID Back</th>
                    <th>Risk</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
              <tbody>
                {kycSubmissions.map(sub => (
                  <tr key={sub.id}>
                    <td data-label="ID">{sub.id}</td>
                    <td data-label="First Name">{sub.firstName || '-'}</td>
                    <td data-label="Last Name">{sub.lastName || '-'}</td>
                    <td data-label="Email">{sub.email || '-'}</td>
                    <td data-label="Mobile">{sub.phone || sub.mobileNumber || '-'}</td>
                    <td data-label="Submitted">{new Date(sub.submittedAt).toLocaleString()}</td>
                    <td data-label="Utility Bill">{sub.utilityBillPath ? (
                      <FileTooltip
                        fileName={sub.utilityBillPath.split('/').pop() || 'Utility Bill'}
                        uploadDate={sub.submittedAt}
                      >
                        <div className="doc-actions">
                          <button
                            className="doc-pill doc-pill--blue"
                            onClick={() => setPreviewFile({
                              isOpen: true,
                              url: `${API_BASE}/admin/files-simple/${sub.utilityBillPath || ''}`,
                              fileName: sub.utilityBillPath?.split('/')?.pop() || 'Utility Bill',
                              contentType: 'application/pdf'
                            })}
                            aria-label="View Utility Bill"
                            title="View Utility Bill"
                          >
                            <span className="doc-emoji">📄</span>
                          </button>
                        </div>
                      </FileTooltip>
                    ) : '-'}</td>
                    <td data-label="Passport">{sub.passportPath ? (
                      <FileTooltip
                        fileName={sub.passportPath.split('/').pop() || 'Passport'}
                        uploadDate={sub.submittedAt}
                      >
                        <button
                          className="doc-pill doc-pill--purple"
                          onClick={() => setPreviewFile({
                            isOpen: true,
                            url: `${API_BASE}/admin/files-simple/${sub.passportPath || ''}`,
                            fileName: sub.passportPath?.split('/')?.pop() || 'Passport',
                            contentType: 'application/pdf'
                          })}
                          aria-label="View Passport"
                          title="View Passport"
                        >
                          <span className="doc-emoji">🛂</span>
                        </button>
                      </FileTooltip>
                    ) : '-'}</td>
                    <td data-label="ID Front">{sub.idFrontPath ? (
                      <FileTooltip
                        fileName={sub.idFrontPath.split('/').pop() || 'ID Front'}
                        uploadDate={sub.submittedAt}
                      >
                        <button
                          className="doc-pill doc-pill--teal"
                          onClick={() => setPreviewFile({
                            isOpen: true,
                            url: `${API_BASE}/admin/files-simple/${sub.idFrontPath || ''}`,
                            fileName: sub.idFrontPath?.split('/')?.pop() || 'ID Front',
                            contentType: 'application/pdf'
                          })}
                          aria-label="View ID Front"
                          title="View ID Front"
                        >
                          <span className="doc-emoji">🪪</span>
                        </button>
                      </FileTooltip>
                    ) : '-'}</td>
                    <td data-label="ID Back">{sub.idBackPath ? (
                      <FileTooltip
                        fileName={sub.idBackPath.split('/').pop() || 'ID Back'}
                        uploadDate={sub.submittedAt}
                      >
                        <button
                          className="doc-pill doc-pill--teal"
                          onClick={() => setPreviewFile({
                            isOpen: true,
                            url: `${API_BASE}/admin/files-simple/${sub.idBackPath || ''}`,
                            fileName: sub.idBackPath?.split('/')?.pop() || 'ID Back',
                            contentType: 'application/pdf'
                          })}
                          aria-label="View ID Back"
                          title="View ID Back"
                        >
                          <span className="doc-emoji">🪪</span>
                        </button>
                      </FileTooltip>
                    ) : '-'}</td>
                    <td data-label="Risk">
                      <select
                        value={getRiskLevel(sub.id, undefined, 'kyc')}
                        onChange={(e) => handleRiskLevelChange(sub.id, e.target.value as 'low' | 'medium' | 'high', 'kyc')}
                        className="risk-select"
                        style={{
                          padding: '4px 8px',
                          borderRadius: 4,
                          border: '1px solid #ddd',
                          fontSize: '12px',
                          cursor: 'pointer',
                          minWidth: '80px'
                        }}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </td>
                    <td data-label="Status">{sub.status}</td>
                    <td data-label="Actions">
                      <div className="action-btn-group compliance-actions">
                      <button
                        className="action-btn secondary"
                        onClick={() => handleViewKycDetails(sub)}
                        title="View Details"
                        disabled={kycDetailsLoading}
                      >
                        {kycDetailsLoading ? 'Loading...' : 'View'}
                      </button>
                      <button 
                        className="action-btn success"
                        onClick={() => handleKycStatus(sub.id, 'APPROVED')}
                      >
                        Approve
                      </button>
                      <button 
                        className="action-btn danger"
                        onClick={() => handleKycStatus(sub.id, 'REJECTED')}
                      >
                        Reject
                      </button>
                      <button 
                        className="action-btn secondary"
                        onClick={() => openDocsModal('kyc', sub.id)}
                        title="View Documents"
                      >
                        View Docs
                      </button>
                      <button 
                        className="action-btn secondary"
                        onClick={() => openUploadModal('kyc', sub.id)}
                      >
                        Upload
                      </button>
                    </div>
                    </td>
                  </tr>
            ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ marginTop: 16 }}>
            <button disabled={kycPage === 1} onClick={() => setKycPage(kycPage - 1)}>Prev</button>
            <span style={{ margin: '0 8px' }}>Page {kycPage} of {kycTotalPages}</span>
            <button disabled={kycPage === kycTotalPages} onClick={() => setKycPage(kycPage + 1)}>Next</button>
          </div>
        </div>
      )}

      {activeTab === 'kyb' && hasPermission('VIEW_ADMIN_COMPLIANCE_KYB_REVIEW') && (
        <div className="compliance-tab-content">
          <div className="compliance-tab-header">
          <h2>Enhanced KYB Review</h2>
            <div className="compliance-filters">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label htmlFor="product-filter-kyb" style={{ fontWeight: 'bold' }}>Filter by Product:</label>
              <select
                id="product-filter-kyb"
                value={selectedProductId !== undefined ? String(selectedProductId) : (currentAdmin?.role === 'SUPER_ADMIN' || (currentAdmin?.permissions || []).includes('VIEW_ADMIN_REPORTS') ? 'ALL' : '')}
                  onChange={(e) => {
                    setSelectedProductId(e.target.value === 'ALL' ? undefined : (e.target.value ? Number(e.target.value) : undefined));
                    setKybPage(1); // Reset to page 1 when filter changes
                  }}
                style={{ padding: '8px 12px', borderRadius: 4, border: '1px solid #ddd', minWidth: 240 }}
                disabled={productsLoading}
              >
                {(currentAdmin?.role === 'SUPER_ADMIN' || (currentAdmin?.permissions || []).includes('VIEW_ADMIN_REPORTS')) && (
                  <option value="ALL">All Products</option>
                )}
                {productsLoading && <option disabled>Loading products...</option>}
                {products.length === 0 && !productsLoading && <option disabled>No products available</option>}
                {products.map(product => (
                  <option key={product.id} value={String(product.id)}>
                    {product.name} ({product.type})
                  </option>
                ))}
              </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label htmlFor="status-filter-kyb" style={{ fontWeight: 'bold' }}>Filter by Status:</label>
                <select
                  id="status-filter-kyb"
                  value={kybStatusFilter}
                  onChange={(e) => {
                    setKybStatusFilter(e.target.value);
                    setKybPage(1); // Reset to page 1 when filter changes
                  }}
                  style={{ padding: '8px 12px', borderRadius: 4, border: '1px solid #ddd', minWidth: 180 }}
                >
                  <option value="">All Statuses</option>
                  <option value="PENDING">PENDING</option>
                  <option value="APPROVED">APPROVED</option>
                  <option value="REJECTED">REJECTED</option>
                </select>
              </div>
            </div>
          </div>
          {kybLoading ? <div>Loading...</div> : kybError ? <div style={{ color: 'red' }}>{kybError}</div> : (
            <div className="admin-table-container">
              {/* Mobile scroll indicator */}
              <div className="table-scroll-indicator">
                ← Swipe horizontally to see all columns →
              </div>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Email</th>
                    <th>Submitted</th>
                    <th>Memorandum</th>
                    <th>Articles</th>
                    <th>Business Utility Bill</th>
                    <th>Risk</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
              <tbody>
                {kybSubmissions.map(sub => (
                  <tr key={sub.id}>
                    <td data-label="ID">{sub.id}</td>
                    <td data-label="Email">{sub.email || '-'}</td>
                    <td data-label="Submitted">{new Date(sub.submittedAt).toLocaleString()}</td>
                    <td data-label="Memorandum">{sub.memorandumPath ? (
                      <FileTooltip
                        fileName={sub.memorandumPath.split('/').pop() || 'Memorandum of Association'}
                        uploadDate={sub.submittedAt}
                      >
                        <button
                          className="doc-pill doc-pill--purple"
                          onClick={() => setPreviewFile({
                            isOpen: true,
                            url: `${API_BASE}/admin/files-simple/${sub.memorandumPath || ''}`,
                            fileName: sub.memorandumPath?.split('/')?.pop() || 'Memorandum of Association',
                            contentType: 'application/pdf'
                          })}
                          aria-label="View Memorandum of Association"
                          title="View Memorandum of Association"
                        >
                          <span className="doc-emoji">📄</span>
                        </button>
                      </FileTooltip>
                    ) : '-'}</td>
                    <td data-label="Articles">{sub.articlesPath ? (
                      <FileTooltip
                        fileName={sub.articlesPath.split('/').pop() || 'Articles of Association'}
                        uploadDate={sub.submittedAt}
                      >
                        <button
                          className="doc-pill doc-pill--purple"
                          onClick={() => setPreviewFile({
                            isOpen: true,
                            url: `${API_BASE}/admin/files-simple/${sub.articlesPath || ''}`,
                            fileName: sub.articlesPath?.split('/')?.pop() || 'Articles of Association',
                            contentType: 'application/pdf'
                          })}
                          aria-label="View Articles of Association"
                          title="View Articles of Association"
                        >
                          <span className="doc-emoji">📄</span>
                        </button>
                      </FileTooltip>
                    ) : '-'}</td>
                    <td data-label="Business Utility Bill">{sub.businessUtilityBillPath ? (
                      <FileTooltip
                        fileName={sub.businessUtilityBillPath.split('/').pop() || 'Business Utility Bill'}
                        uploadDate={sub.submittedAt}
                      >
                        <button
                          className="doc-pill doc-pill--blue"
                          onClick={() => setPreviewFile({
                            isOpen: true,
                            url: `${API_BASE}/admin/files-simple/${sub.businessUtilityBillPath || ''}`,
                            fileName: sub.businessUtilityBillPath?.split('/')?.pop() || 'Business Utility Bill',
                            contentType: 'application/pdf'
                          })}
                          aria-label="View Business Utility Bill"
                          title="View Business Utility Bill"
                        >
                          <span className="doc-emoji">📄</span>
                        </button>
                      </FileTooltip>
                    ) : '-'}</td>
                    <td data-label="Risk">
                      <select
                        value={getRiskLevel(sub.id, sub.industry, 'kyb')}
                        onChange={(e) => handleRiskLevelChange(sub.id, e.target.value as 'low' | 'medium' | 'high', 'kyb')}
                        className="risk-select"
                        style={{
                          padding: '4px 8px',
                          borderRadius: 4,
                          border: '1px solid #ddd',
                          fontSize: '12px',
                          cursor: 'pointer',
                          minWidth: '80px'
                        }}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </td>
                    <td data-label="Status">{sub.status}</td>
                    <td data-label="Actions">
                      <div className="action-btn-group">
                        <button
                          className="action-btn success"
                          onClick={() => handleKybStatus(sub.id, 'APPROVED')}
                          title="Approve Registration"
                        >
                          ✅ Approve
                        </button>
                        <button
                          className="action-btn danger"
                          onClick={() => handleKybStatus(sub.id, 'REJECTED')}
                          title="Reject Registration"
                        >
                          ❌ Reject
                        </button>
                        <button
                          className="action-btn secondary"
                          onClick={() => openDocsModal('kyb', sub.id)}
                          title="View Documents"
                        >
                          View Docs
                        </button>
                        <button
                          className="action-btn secondary"
                          onClick={() => openUploadModal('kyb', sub.id)}
                          title="Upload Documents"
                        >
                          ⬆️ Upload
                        </button>
                        <button
                          className="action-btn secondary"
                          onClick={() => handleViewKybDetails(sub)}
                          title="View Details"
                        >
                          👁️ View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
          <div style={{ marginTop: 16 }}>
            <button disabled={kybPage === 1} onClick={() => setKybPage(kybPage - 1)}>Prev</button>
            <span style={{ margin: '0 8px' }}>Page {kybPage} of {kybTotalPages}</span>
            <button disabled={kybPage === kybTotalPages} onClick={() => setKybPage(kybPage + 1)}>Next</button>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      <UserDetailsModal
        isOpen={userDetailsModal.isOpen}
        onClose={() => setUserDetailsModal({ isOpen: false, alert: null })}
        alert={userDetailsModal.alert}
        onAssign={handleAssignFromModal}
        onUpdateStatus={handleUpdateStatus}
      />

      {/* KYB Details Modal */}
      <KybDetailsModal
        open={selectedKyb !== null}
        onClose={() => setSelectedKyb(null)}
        submission={selectedKyb}
      />

      {isDocsOpen && (
        <div className="modal-backdrop">
          <div className="modal" role="dialog" aria-modal="true">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Submission Documents</h3>
              <button className="action-btn secondary" onClick={() => setIsDocsOpen(false)}>Close</button>
            </div>
            {docsLoading ? (
              <div>Loading...</div>
            ) : docsError ? (
              <div style={{ color: 'red' }}>{docsError}</div>
            ) : attachments.length === 0 ? (
              <div>No documents found.</div>
            ) : (
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                <table className="admin-table" key={docsVersion}>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Filename</th>
                      <th>Uploaded By</th>
                      <th>Uploaded At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedAttachments.map((att, index) => (
                      <tr key={`${att.id ?? 'synthetic'}-${att.documentId ?? att.type ?? index}`}>
                        <td>{att.type}</td>
                        <td>{att.filename || '-'}</td>
                        <td>{att.uploadedBy || '-'}</td>
                        <td>{att.uploadedAt ? new Date(att.uploadedAt).toLocaleString() : '-'}</td>
                        <td>
                          <button
                            className="action-btn secondary"
                            onClick={() => setPreviewFile({
                              isOpen: true,
                              url: `${API_BASE}/admin/files-simple/${att.documentId}`,
                              fileName: att.filename || 'Document',
                              contentType: att.contentType || 'application/pdf'
                            })}
                          >
                            Preview
                          </button>
                          <button
                            className="action-btn secondary"
                            onClick={() => openAttachment(att)}
                            style={{ marginLeft: 6 }}
                          >
                            Open
                          </button>
                          <button
                            className="action-btn secondary"
                            onClick={() => downloadAttachment(att)}
                            style={{ marginLeft: 6 }}
                          >
                            Download
                          </button>
                          {att.id && att.id !== -1 && (
                            <button
                              className="action-btn danger"
                              onClick={async () => {
                                if (!window.confirm('Delete this attachment?')) return;
                                const res = await adminApiService.deleteAttachment(att.id as unknown as number);
                                if (!res.success) {
                                  alert(res.error || 'Delete failed');
                                  return;
                                }
                                // Refresh list for current modal
                                if (docsTarget) {
                                  await refreshDocsList(docsTarget.kind, docsTarget.id);
                                }
                              }}
                              style={{ marginLeft: 6 }}
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Showing {(attachments.length === 0 ? 0 : (docsPage - 1) * docsPageSize + 1)}-
                    {Math.min(docsPage * docsPageSize, attachments.length)} of {attachments.length}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="action-btn secondary"
                      onClick={() => setDocsPage(Math.max(1, docsPage - 1))}
                      disabled={docsPage === 1}
                    >
                      Prev
                    </button>
                    <span style={{ alignSelf: 'center', fontSize: 12 }}>Page {docsPage} of {docsTotalPages}</span>
                    <button
                      className="action-btn secondary"
                      onClick={() => setDocsPage(Math.min(docsTotalPages, docsPage + 1))}
                      disabled={docsPage === docsTotalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isUploadOpen && (
        <div className="modal-backdrop">
          <div className="modal" role="dialog" aria-modal="true">
            <h3>Upload Documents</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label>
                Document Type
                <select value={uploadType} onChange={e => setUploadType(e.target.value)} style={{ marginLeft: 8 }}>
                  <option value="UTILITY_BILL">UTILITY_BILL</option>
                  <option value="PASSPORT">PASSPORT</option>
                  <option value="ID_FRONT">ID_FRONT</option>
                  <option value="ID_BACK">ID_BACK</option>
                  <option value="BOARD_RESOLUTION">BOARD_RESOLUTION</option>
                  <option value="BENEFICIAL_OWNERSHIP">BENEFICIAL_OWNERSHIP</option>
                  <option value="FINANCIAL_STATEMENTS">FINANCIAL_STATEMENTS</option>
                  <option value="BUSINESS_PLAN">BUSINESS_PLAN</option>
                  <option value="OTHER">OTHER</option>
                </select>
              </label>
              <label>
                Notes
                <input type="text" value={uploadNote} onChange={e => setUploadNote(e.target.value)} style={{ width: '100%', marginTop: 4 }} />
              </label>
              <input
                ref={fileInputRef}
                name="files"
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={e => {
                  const list = Array.from(e.target.files || []);
                  console.log('Upload picker selected files:', list.map(f => `${f.name} (${f.type}) ${f.size}b`));
                  setUploadFiles(list);
                }}
              />
              {uploadFiles.length > 0 && (
                <div style={{ fontSize: 12, color: '#666' }}>{uploadFiles.length} file(s) selected</div>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="action-btn secondary" onClick={() => setIsUploadOpen(false)} disabled={uploading}>Cancel</button>
                <button className="action-btn success" onClick={submitUpload} disabled={uploading || uploadFiles.length === 0}>{uploading ? 'Uploading...' : 'Upload'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KYC Details Modal */}
      <KycDetailsModal
        open={selectedKyc !== null}
        onClose={() => setSelectedKyc(null)}
        submission={selectedKyc}
      />

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={previewFile.isOpen}
        onClose={() => setPreviewFile({ isOpen: false, url: '', fileName: '', contentType: undefined })}
        fileUrl={previewFile.url}
        fileName={previewFile.fileName}
        contentType={previewFile.contentType}
      />
    </div>
  );
});

ComplianceAlerts.displayName = 'ComplianceAlerts';

export default ComplianceAlerts; 